/**
 * DecisionTimelinePanel.jsx
 * ==========================
 * AI Decision Timeline visualization.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Clock, Upload, FileText, Tag, Search, Shield, 
  GitBranch, CheckCircle2, XCircle, Brain, Database,
  RefreshCw, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  upload: Upload,
  ocr: FileText,
  classification: Tag,
  entity_extraction: Search,
  risk_analysis: Shield,
  workflow_suggestion: GitBranch,
  approval_pending: Clock,
  approved: CheckCircle2,
  rejected: XCircle,
  ai_insight: Brain,
  memory_indexed: Database
};

const COLORS = {
  blue: 'bg-blue-100 text-blue-700 border-blue-300',
  purple: 'bg-purple-100 text-purple-700 border-purple-300',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  orange: 'bg-orange-100 text-orange-700 border-orange-300',
  green: 'bg-green-100 text-green-700 border-green-300',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  red: 'bg-red-100 text-red-700 border-red-300',
  violet: 'bg-violet-100 text-violet-700 border-violet-300',
  teal: 'bg-teal-100 text-teal-700 border-teal-300'
};

const DecisionTimelinePanel = ({ documentId }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (documentId) {
      fetchTimeline();
    }
  }, [documentId]);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(
        `${API_URL}/api/agentic-extensions/decision-timeline/${documentId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTimeline(data);
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString();
  };

  if (!documentId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Select a document to view its decision timeline</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="decision-timeline-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle>AI Decision Timeline</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTimeline}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        <CardDescription>
          Track AI processing decisions and insights
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {timeline?.success && (
          <div className="space-y-6">
            {/* Current Status */}
            <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
              <div>
                <span className="text-sm text-muted-foreground">Current Status</span>
                <div className="font-medium">{timeline.current_status?.status || 'Active'}</div>
              </div>
              {timeline.current_status?.risk_level && (
                <Badge variant={
                  timeline.current_status.risk_level === 'High' ? 'destructive' :
                  timeline.current_status.risk_level === 'Medium' ? 'warning' : 'success'
                }>
                  {timeline.current_status.risk_level} Risk
                </Badge>
              )}
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

              {/* Events */}
              <div className="space-y-4">
                {timeline.timeline?.map((event, index) => {
                  const Icon = ICONS[event.event_type] || Clock;
                  const colorClass = COLORS[event.color] || COLORS.blue;

                  return (
                    <div key={index} className="relative pl-12">
                      {/* Icon */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center border-2 ${colorClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>

                      {/* Content */}
                      <div className="bg-background border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{event.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(event.timestamp)}
                          </span>
                        </div>
                        
                        {event.details && Object.keys(event.details).length > 0 && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            {Object.entries(event.details).map(([key, value]) => (
                              value && (
                                <div key={key} className="flex gap-2">
                                  <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                                  <span className="font-medium">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                  </span>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AI Decisions */}
            {timeline.ai_decisions?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4 text-primary" />
                  AI Decisions & Recommendations
                </h4>
                <div className="space-y-2">
                  {timeline.ai_decisions.map((decision, idx) => (
                    <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{decision.decision}</span>
                        {decision.confidence && (
                          <Badge variant="outline">
                            {Math.round(decision.confidence * 100)}% confidence
                          </Badge>
                        )}
                      </div>
                      {decision.reasoning && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {decision.reasoning}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {timeline && !timeline.success && (
          <div className="text-center py-8 text-muted-foreground">
            <p>{timeline.error || 'Failed to load timeline'}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DecisionTimelinePanel;
